import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useI18n } from '../context/I18nContext';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Monitor, Laptop, Terminal, Smartphone, Download, Clock, LogIn, Apple, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { API } from '../config';

const platformConfig = [
  { id: 'windows', icon: Monitor, colorKey: 'blue' },
  { id: 'macos', icon: Laptop, colorKey: 'zinc' },
  { id: 'linux', icon: Terminal, colorKey: 'zinc' },
  { id: 'android', icon: Smartphone, colorKey: 'zinc' },
  { id: 'ios', icon: Smartphone, colorKey: 'zinc' },
];

const platformNames = { windows: 'Windows', macos: 'macOS', linux: 'Linux', android: 'Android', ios: 'iOS' };

const colorMap = {
  blue: {
    bg: 'bg-blue-500/10', border: 'border-blue-500/20', hoverBorder: 'hover:border-blue-500/40',
    text: 'text-blue-500', btn: 'bg-blue-600 hover:bg-blue-500 text-white shadow-[0_0_15px_rgba(59,130,246,0.3)]',
  },
  emerald: {
    bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', hoverBorder: 'hover:border-emerald-500/40',
    text: 'text-emerald-500', btn: 'bg-emerald-600 hover:bg-emerald-500 text-white shadow-[0_0_15px_rgba(16,185,129,0.3)]',
  },
  zinc: {
    bg: 'bg-secondary', border: 'border-border', hoverBorder: 'hover:border-border',
    text: 'text-muted-foreground', btn: 'bg-secondary text-muted-foreground cursor-not-allowed',
  },
};

const bannerLabels = {
  en: { title: 'Sign in to download HexoPDF', subtitle: 'Create a free account to access downloads and manage your licenses.', cta: 'Sign in' },
  fr: { title: 'Connectez-vous pour t\u00e9l\u00e9charger HexoPDF', subtitle: 'Cr\u00e9ez un compte gratuit pour acc\u00e9der aux t\u00e9l\u00e9chargements et g\u00e9rer vos licences.', cta: 'Se connecter' },
  es: { title: 'Inicia sesi\u00f3n para descargar HexoPDF', subtitle: 'Crea una cuenta gratuita para acceder a las descargas y gestionar tus licencias.', cta: 'Iniciar sesi\u00f3n' },
  de: { title: 'Melden Sie sich an, um HexoPDF herunterzuladen', subtitle: 'Erstellen Sie ein kostenloses Konto, um auf Downloads zuzugreifen und Ihre Lizenzen zu verwalten.', cta: 'Anmelden' },
  it: { title: 'Accedi per scaricare HexoPDF', subtitle: 'Crea un account gratuito per accedere ai download e gestire le tue licenze.', cta: 'Accedi' },
  pt: { title: 'Entre para baixar o HexoPDF', subtitle: 'Crie uma conta gratuita para acessar downloads e gerenciar suas licen\u00e7as.', cta: 'Entrar' },
  nl: { title: 'Log in om HexoPDF te downloaden', subtitle: 'Maak een gratis account aan om downloads te openen en je licenties te beheren.', cta: 'Inloggen' },
  pl: { title: 'Zaloguj si\u0119, aby pobra\u0107 HexoPDF', subtitle: 'Utw\u00f3rz darmowe konto, aby uzyska\u0107 dost\u0119p do pobra\u0144 i zarz\u0105dza\u0107 licencjami.', cta: 'Zaloguj' },
  tr: { title: 'HexoPDF indirmek i\u00e7in giri\u015f yap\u0131n', subtitle: '\u0130ndirmelere eri\u015fmek ve lisanslar\u0131n\u0131z\u0131 y\u00f6netmek i\u00e7in \u00fccretsiz bir hesap olu\u015fturun.', cta: 'Giri\u015f yap' },
  ru: { title: '\u0412\u043e\u0439\u0434\u0438\u0442\u0435, \u0447\u0442\u043e\u0431\u044b \u0441\u043a\u0430\u0447\u0430\u0442\u044c HexoPDF', subtitle: '\u0421\u043e\u0437\u0434\u0430\u0439\u0442\u0435 \u0431\u0435\u0441\u043f\u043b\u0430\u0442\u043d\u044b\u0439 \u0430\u043a\u043a\u0430\u0443\u043d\u0442 \u0434\u043b\u044f \u0434\u043e\u0441\u0442\u0443\u043f\u0430 \u043a \u0437\u0430\u0433\u0440\u0443\u0437\u043a\u0430\u043c.', cta: '\u0412\u043e\u0439\u0442\u0438' },
  ja: { title: 'HexoPDF\u3092\u30c0\u30a6\u30f3\u30ed\u30fc\u30c9\u3059\u308b\u306b\u306f\u30ed\u30b0\u30a4\u30f3', subtitle: '\u7121\u6599\u30a2\u30ab\u30a6\u30f3\u30c8\u3092\u4f5c\u6210\u3057\u3066\u30c0\u30a6\u30f3\u30ed\u30fc\u30c9\u306b\u30a2\u30af\u30bb\u30b9\u3002', cta: '\u30ed\u30b0\u30a4\u30f3' },
  zh: { title: '\u767b\u5f55\u4ee5\u4e0b\u8f7dHexoPDF', subtitle: '\u521b\u5efa\u514d\u8d39\u8d26\u6237\u4ee5\u8bbf\u95ee\u4e0b\u8f7d\u3002', cta: '\u767b\u5f55' },
  ar: { title: '\u0633\u062c\u0644 \u0627\u0644\u062f\u062e\u0648\u0644 \u0644\u062a\u0646\u0632\u064a\u0644 HexoPDF', subtitle: '\u0623\u0646\u0634\u0626 \u062d\u0633\u0627\u0628\u064b\u0627 \u0645\u062c\u0627\u0646\u064a\u064b\u0627 \u0644\u0644\u0648\u0635\u0648\u0644 \u0625\u0644\u0649 \u0627\u0644\u062a\u0646\u0632\u064a\u0644\u0627\u062a.', cta: '\u062a\u0633\u062c\u064a\u0644 \u0627\u0644\u062f\u062e\u0648\u0644' },
  hi: { title: 'HexoPDF डाउनलोड करने के लिए साइन इन करें', subtitle: 'डाउनलोड और लाइसेंस प्रबंधित करने के लिए मुफ़्त खाता बनाएं।', cta: 'साइन इन' },
};

const descLabels = {
  en: { windows: 'Windows 10 / 11 (64-bit)', macos: 'macOS 12 Monterey and later', linux: 'Ubuntu, Fedora, Debian', android: 'Android 10+', ios: 'iOS 15+', coming: 'Coming Soon', download: 'Download', version: 'Version' },
  fr: { windows: 'Windows 10 / 11 (64-bit)', macos: 'macOS 12 Monterey et ultérieur', linux: 'Ubuntu, Fedora, Debian', android: 'Android 10+', ios: 'iOS 15+', coming: 'Bientôt disponible', download: 'Télécharger', version: 'Version' },
  es: { windows: 'Windows 10 / 11 (64-bit)', macos: 'macOS 12 Monterey y posterior', linux: 'Ubuntu, Fedora, Debian', android: 'Android 10+', ios: 'iOS 15+', coming: 'Próximamente', download: 'Descargar', version: 'Versión' },
  de: { windows: 'Windows 10 / 11 (64-bit)', macos: 'macOS 12 Monterey und neuer', linux: 'Ubuntu, Fedora, Debian', android: 'Android 10+', ios: 'iOS 15+', coming: 'Demnächst', download: 'Herunterladen', version: 'Version' },
  it: { windows: 'Windows 10 / 11 (64-bit)', macos: 'macOS 12 Monterey e successivi', linux: 'Ubuntu, Fedora, Debian', android: 'Android 10+', ios: 'iOS 15+', coming: 'Prossimamente', download: 'Scarica', version: 'Versione' },
  pt: { windows: 'Windows 10 / 11 (64-bit)', macos: 'macOS 12 Monterey e posterior', linux: 'Ubuntu, Fedora, Debian', android: 'Android 10+', ios: 'iOS 15+', coming: 'Em breve', download: 'Baixar', version: 'Versão' },
  nl: { windows: 'Windows 10 / 11 (64-bit)', macos: 'macOS 12 Monterey en nieuwer', linux: 'Ubuntu, Fedora, Debian', android: 'Android 10+', ios: 'iOS 15+', coming: 'Binnenkort', download: 'Downloaden', version: 'Versie' },
  pl: { windows: 'Windows 10 / 11 (64-bit)', macos: 'macOS 12 Monterey i nowsze', linux: 'Ubuntu, Fedora, Debian', android: 'Android 10+', ios: 'iOS 15+', coming: 'Wkrótce', download: 'Pobierz', version: 'Wersja' },
  tr: { windows: 'Windows 10 / 11 (64-bit)', macos: 'macOS 12 Monterey ve sonrası', linux: 'Ubuntu, Fedora, Debian', android: 'Android 10+', ios: 'iOS 15+', coming: 'Yakında', download: 'İndir', version: 'Sürüm' },
  ru: { windows: 'Windows 10 / 11 (64-bit)', macos: 'macOS 12 Monterey и новее', linux: 'Ubuntu, Fedora, Debian', android: 'Android 10+', ios: 'iOS 15+', coming: 'Скоро', download: 'Скачать', version: 'Версия' },
  ja: { windows: 'Windows 10 / 11 (64-bit)', macos: 'macOS 12 Monterey以降', linux: 'Ubuntu, Fedora, Debian', android: 'Android 10+', ios: 'iOS 15+', coming: '近日公開', download: 'ダウンロード', version: 'バージョン' },
  zh: { windows: 'Windows 10 / 11 (64-bit)', macos: 'macOS 12 Monterey及更高版本', linux: 'Ubuntu, Fedora, Debian', android: 'Android 10+', ios: 'iOS 15+', coming: '即将推出', download: '下载', version: '版本' },
  ar: { windows: 'Windows 10 / 11 (64-bit)', macos: 'macOS 12 Monterey والأحدث', linux: 'Ubuntu, Fedora, Debian', android: 'Android 10+', ios: 'iOS 15+', coming: 'قريبًا', download: 'تحميل', version: 'الإصدار' },
  hi: { windows: 'Windows 10 / 11 (64-bit)', macos: 'macOS 12 Monterey और बाद के', linux: 'Ubuntu, Fedora, Debian', android: 'Android 10+', ios: 'iOS 15+', coming: 'जल्द आ रहा है', download: 'डाउनलोड', version: 'संस्करण' },
};

export default function DownloadsPage() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const banner = bannerLabels[lang] || bannerLabels.en;
  const dl = descLabels[lang] || descLabels.en;
  const [latestVersions, setLatestVersions] = useState({});

  useEffect(() => {
    axios.get(`${API}/updates/products`).then(r => {
      const map = {};
      (r.data.products || []).forEach(p => { map[p.product_slug] = p; });
      setLatestVersions(map);
    }).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-background" data-testid="downloads-page">
      <Navbar />
      <section className="pt-32 pb-24 md:pb-32">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-16 text-center">
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-foreground mb-4" data-testid="downloads-title">
              {t('downloads.title')}
            </h1>
            <p className="text-muted-foreground text-base md:text-lg max-w-xl mx-auto">
              {t('downloads.subtitle')}
            </p>
          </motion.div>

          {!user && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              className="mb-10 p-6 rounded-2xl bg-blue-500/5 border border-blue-500/20 text-center max-w-3xl mx-auto" data-testid="downloads-login-banner">
              <LogIn className="w-8 h-8 text-blue-500 mx-auto mb-3" />
              <h3 className="text-foreground font-semibold text-lg mb-1">{banner.title}</h3>
              <p className="text-muted-foreground text-sm mb-4">{banner.subtitle}</p>
              <Button onClick={() => navigate('/login')} className="bg-blue-600 hover:bg-blue-500 text-white rounded-full px-8 h-10 font-medium shadow-[0_0_15px_rgba(59,130,246,0.3)]" data-testid="downloads-login-btn">
                {banner.cta}
              </Button>
            </motion.div>
          )}

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {platformConfig.map((platform, i) => {
              const Icon = platform.icon;
              const latest = latestVersions[platform.id];
              const hasUpdate = !!latest;
              const cKey = hasUpdate ? (platform.id === 'windows' ? 'blue' : 'emerald') : 'zinc';
              const c = colorMap[cKey];

              return (
                <motion.div key={platform.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}
                  className={`relative p-6 rounded-2xl ${c.bg} border ${c.border} ${c.hoverBorder} transition-all ${!user ? 'opacity-60' : ''}`}
                  data-testid={`download-card-${platform.id}`}>
                  <div className={`w-10 h-10 rounded-xl ${c.bg} border ${c.border} flex items-center justify-center mb-4`}>
                    <Icon className={`w-5 h-5 ${c.text}`} />
                  </div>
                  <h3 className="text-foreground font-semibold text-lg mb-0.5">{platformNames[platform.id]}</h3>
                  <p className="text-muted-foreground text-xs mb-1">{dl[platform.id]}</p>
                  {hasUpdate && (
                    <div className="flex items-center gap-1.5 mb-3">
                      <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                      <span className="text-[11px] text-emerald-400 font-mono">{dl.version} {latest.version}</span>
                      {latest.file_size && <span className="text-[10px] text-muted-foreground/60">· {latest.file_size}</span>}
                    </div>
                  )}
                  {!hasUpdate && <div className="mb-3" />}
                  {hasUpdate ? (
                    <a
                      href={user ? latest.download_url : undefined}
                      onClick={e => { if (!user) { e.preventDefault(); navigate('/login'); } }}
                      className={`w-full inline-flex items-center justify-center rounded-full h-10 font-medium text-sm transition-all ${user ? c.btn : colorMap.zinc.btn}`}
                      data-testid={`download-btn-${platform.id}`}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      {dl.download} {platformNames[platform.id]}
                    </a>
                  ) : (
                    <Button disabled className={`w-full rounded-full h-10 font-medium ${colorMap.zinc.btn}`} data-testid={`download-btn-${platform.id}`}>
                      <Clock className="w-4 h-4 mr-2" />
                      {dl.coming}
                    </Button>
                  )}
                  {hasUpdate && latest.release_notes && (
                    <p className="text-[10px] text-muted-foreground/50 mt-2 line-clamp-2">{latest.release_notes}</p>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
